import { useSidebar } from 'mastodon/components/use-sidebar';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

function RouteAnimate({
  children,
  intensity = 'micro',
  swipeThreshold = 80,
  enableSwipeBack = true,
  enableHapticFeedback = true,
}) {
  const [animationClass, setAnimationClass] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef('');
  const { closeSidebar, isVisible, isTransitioning } = useSidebar();
  const animationFrameRef = useRef(null);
  const timeoutRef = useRef(null);

  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const touchCurrentRef = useRef({ x: 0, y: 0 });
  const isSwipingRef = useRef(false);
  const hasTriggeredHapticRef = useRef(false);
  const swipeVelocityThreshold = 0.3;

  const isAnimatingRef = useRef(false);
  const hasNavigatedRef = useRef(false);
  const gestureIdRef = useRef(0);
  const lastTouchTimeRef = useRef(0);

  const gestureStartedRef = useRef(false);
  const gestureTypeRef = useRef('none');
  const navigationCompleteRef = useRef(false);

  const triggerHaptic = useCallback(
    (type = 'light') => {
      if (!enableHapticFeedback || !navigator.vibrate) return;
      switch (type) {
        case 'light':
          navigator.vibrate(10);
          break;
        case 'medium':
          navigator.vibrate(20);
          break;
        case 'success':
          navigator.vibrate([10, 50, 10]);
          break;
      }
    },
    [enableHapticFeedback],
  );

  const resetGestureState = useCallback(() => {
    isSwipingRef.current = false;
    hasTriggeredHapticRef.current = false;
    gestureStartedRef.current = false;
    gestureTypeRef.current = 'none';
    isAnimatingRef.current = false;
    
    setSwipeOffset(0);
    setIsSwipeActive(false);
    
    document.body.style.overflow = '';
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    gestureIdRef.current = Math.random();
  }, []);

  const handleResize = useCallback(() => {
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      setIsMobile(window.innerWidth <= 768);
      setScreenWidth(window.innerWidth);
      resetGestureState();
    }, 100);
  }, [resetGestureState]);

  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      clearTimeout(timeoutRef.current);
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const getRouteDirection = useCallback((prevPath, currentPath) => {
    const leftRoutes = ['/home', '/public/local'];

    const isValidUserPostPath = (path) => {
      const parts = path.split('/');
      return path.startsWith('/@') && parts.length >= 3 && parts[2] !== '';
    };

    const isLeftRoute = leftRoutes.some((route) => currentPath.startsWith(route)) || isValidUserPostPath(currentPath);
    const isPrevLeftRoute = leftRoutes.some((route) => prevPath.startsWith(route)) || isValidUserPostPath(prevPath);

    if (!isLeftRoute && !isPrevLeftRoute) return 'none';
    if (isLeftRoute && !isPrevLeftRoute) return 'left';
    if (!isLeftRoute && isPrevLeftRoute) return 'right';

    return 'left';
  }, []);

  const runAnimation = useCallback(
    (direction) => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      clearTimeout(timeoutRef.current);

      animationFrameRef.current = requestAnimationFrame(() => {
        const prefix = intensity === 'micro' ? 'micro' : 'butter';

        if (isMobile && direction !== 'none') {
          setAnimationClass(`${prefix}-slide-${direction}`);
          timeoutRef.current = setTimeout(
            () => {
              setAnimationClass('');
            },
            intensity === 'micro' ? 200 : 280,
          );
        } else {
          setAnimationClass(`${prefix}-fade`);
          timeoutRef.current = setTimeout(
            () => {
              setAnimationClass('');
            },
            intensity === 'micro' ? 150 : 180,
          );
        }
      });
    },
    [isMobile, intensity],
  );

  const handleTouchStart = useCallback(
    (e) => {
      navigationCompleteRef.current = false;
      
      if (!isMobile || !enableSwipeBack || ['/home', '/public/local'].includes(pathname)) {
        return;
      }

      const isValidUserPostPath = pathname.match(/^\/@[^/]+\/[^/]+$/);
      if (!isValidUserPostPath) return;

      if (isAnimatingRef.current || hasNavigatedRef.current) {
        return;
      }

      const now = Date.now();
      if (now - lastTouchTimeRef.current < 100) {
        return;
      }
      lastTouchTimeRef.current = now;

      const touch = e.touches[0];
      
      if (touch.clientX > 50) {
        return;
      }
      
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
      touchCurrentRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };
      
      gestureStartedRef.current = true;
      gestureTypeRef.current = 'none';
      isSwipingRef.current = false;
      hasTriggeredHapticRef.current = false;
      gestureIdRef.current = Math.random();
    },
    [isMobile, enableSwipeBack, pathname],
  );

  const handleTouchMove = useCallback(
    (e) => {
      if (!isMobile || !enableSwipeBack || !gestureStartedRef.current) return;

      if (isAnimatingRef.current || hasNavigatedRef.current || navigationCompleteRef.current) {
        return;
      }

      const touch = e.touches[0];
      touchCurrentRef.current = {
        x: touch.clientX,
        y: touch.clientY,
      };

      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;

      const absDeltaX = Math.abs(deltaX);
      const absDeltaY = Math.abs(deltaY);

      if (gestureTypeRef.current === 'none' && (absDeltaX > 10 || absDeltaY > 10)) {
        if (absDeltaX > absDeltaY && absDeltaX > 15) {
          if (deltaX > 0 && touchStartRef.current.x <= 50) {
            gestureTypeRef.current = 'swipe';
          } else {
            gestureTypeRef.current = 'scroll';
            return;
          }
        } else {
          gestureTypeRef.current = 'scroll';
          return;
        }
      }

      if (gestureTypeRef.current !== 'swipe') {
        return;
      }

      const isHorizontalGesture =
        absDeltaX > 20 &&
        absDeltaX > absDeltaY * 2 &&
        absDeltaY < 30 &&
        deltaX > 0;

      if (isHorizontalGesture) {
        if (!isSwipingRef.current) {
          isSwipingRef.current = true;
          setIsSwipeActive(true);
          triggerHaptic('light');
          document.body.style.overflow = 'hidden';
        }

        if (deltaX > 0) {
          const absDistance = Math.abs(deltaX);
          let adjustedOffset = deltaX;

          const resistancePoint = screenWidth * 0.8;
          if (absDistance > resistancePoint) {
            const excess = absDistance - resistancePoint;
            const maxExcess = screenWidth * 0.2;
            const resistance = Math.sqrt(excess / maxExcess);
            adjustedOffset = resistancePoint + excess * (1 - resistance * 0.5);
          }

          setSwipeOffset(adjustedOffset);

          const progress = absDistance / swipeThreshold;
          if (progress >= 1 && !hasTriggeredHapticRef.current) {
            triggerHaptic('medium');
            hasTriggeredHapticRef.current = true;
          }

          e.preventDefault();
        }
      }
    },
    [isMobile, enableSwipeBack, screenWidth, swipeThreshold, triggerHaptic],
  );

  const handleTouchEnd = useCallback(() => {
    if (!isMobile || !gestureStartedRef.current || !enableSwipeBack) {
      resetGestureState();
      return;
    }

    if (gestureTypeRef.current !== 'swipe') {
      resetGestureState();
      return;
    }

    if (isAnimatingRef.current || hasNavigatedRef.current || navigationCompleteRef.current) {
      resetGestureState();
      return;
    }

    const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
    const deltaY = touchCurrentRef.current.y - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;
    const velocity = Math.abs(deltaX) / deltaTime;

    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const isRightSwipe = deltaX > 0;
    const isLongEnough = Math.abs(deltaX) > swipeThreshold;
    const isFastEnough = velocity > swipeVelocityThreshold;

    if (isHorizontalSwipe && isRightSwipe && (isLongEnough || isFastEnough)) {
      isAnimatingRef.current = true;
      hasNavigatedRef.current = true;
      navigationCompleteRef.current = true;
      triggerHaptic('success');

      const startOffset = swipeOffset;
      const startTime = performance.now();
      const duration = 300;
      const currentGestureId = gestureIdRef.current;

      const animateOut = (currentTime) => {
        if (currentGestureId !== gestureIdRef.current) {
          return;
        }

        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOut = 1 - Math.pow(1 - progress, 3);

        const currentOffset = startOffset + (screenWidth - startOffset) * easeOut;

        setSwipeOffset(currentOffset);

        if (progress < 1) {
          requestAnimationFrame(animateOut);
        } else {
          if (hasNavigatedRef.current && window.history.length > 1) {
            try {
              window.history.back();
            } catch (error) {
              console.warn('Navigation failed:', error);
            }
          }

          setTimeout(() => {
            resetGestureState();
            
            setTimeout(() => {
              hasNavigatedRef.current = false;
              navigationCompleteRef.current = false;
            }, 1000);
          }, 100);
        }
      };

      requestAnimationFrame(animateOut);
    } else {
      triggerHaptic('light');

      isAnimatingRef.current = true;

      const startOffset = swipeOffset;
      const startTime = performance.now();
      const duration = 200;
      const currentGestureId = gestureIdRef.current;

      const animateCancel = (currentTime) => {
        if (currentGestureId !== gestureIdRef.current) {
          return;
        }

        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);

        const easeOutBounce = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2;

        const currentOffset = startOffset * (1 - easeOutBounce);

        setSwipeOffset(currentOffset);

        if (progress < 1) {
          requestAnimationFrame(animateCancel);
        } else {
          resetGestureState();
        }
      };

      requestAnimationFrame(animateCancel);
    }
  }, [isMobile, enableSwipeBack, swipeThreshold, swipeVelocityThreshold, triggerHaptic, swipeOffset, screenWidth, resetGestureState]);

  useEffect(() => {
    if (!isMobile || !enableSwipeBack) return;

    const element = document.body;
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: false });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isMobile, enableSwipeBack, handleTouchStart, handleTouchMove, handleTouchEnd]);

  useEffect(() => {
    if (prevPathnameRef.current && prevPathnameRef.current !== pathname) {
      resetGestureState();
      
      const direction = getRouteDirection(prevPathnameRef.current, pathname);

      if (isVisible && !isTransitioning) {
        closeSidebar();
        setTimeout(() => runAnimation(direction), 150);
      } else {
        runAnimation(direction);
      }
      
      setTimeout(() => {
        hasNavigatedRef.current = false;
        navigationCompleteRef.current = false;
      }, 500);
    }

    prevPathnameRef.current = pathname;
  }, [pathname, getRouteDirection, runAnimation, isVisible, isTransitioning, closeSidebar, resetGestureState]);

  useEffect(() => {
    return () => {
      resetGestureState();
      clearTimeout(timeoutRef.current);
    };
  }, [resetGestureState]);

  const swipeTransform = isSwipeActive ? `translate3d(${swipeOffset}px, 0, 0)` : '';
  const swipeOpacity = isSwipeActive ? Math.max(0.2, 1 - Math.abs(swipeOffset) / (screenWidth * 0.9)) : 1;

  return (
    <div
      className={`route-animate ${animationClass}`}
      style={{
        transform: swipeTransform,
        opacity: swipeOpacity,
      }}
    >
      {children}

      {isSwipeActive && (
        <div
          className='swipe-background'
          style={{
            opacity: Math.min(Math.abs(swipeOffset) / (screenWidth * 0.5), 0.6),
          }}
        />
      )}
    </div>
  );
}

export default RouteAnimate;